import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRawStocks, deleteRawStock } from '../../redux/actions/rawStock/rawStockAction';
import { fetchStocks } from '../../redux/actions/stock/stockAction';
import { fetchStones } from '../../redux/actions/stone/stoneAction';
import { fetchPurities } from '../../redux/actions/purity/purityAction';
import { fetchColors } from '../../redux/actions/color/colorAction';
import DataTable from 'react-data-table-component';
import { Box, Typography, TextField, Button, IconButton, Alert, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { confirmDelete, showSuccess } from '../../utils/alertUtils';
import PageLoader from '../common/PageLoader';

const DEFAULT_LIMIT = 200;

const RawStockTable = ({ onEdit }) => {
  const dispatch = useDispatch();
  const { rawStocks, loading, error } = useSelector((state) => state.rawStock);
  const { stocks } = useSelector((state) => state.stock);
  const { stones } = useSelector((state) => state.stone);
  const { purities } = useSelector((state) => state.purity);
  const { colors } = useSelector((state) => state.color);
  const { user } = useSelector((state) => state.login);
  const [filterText, setFilterText] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const offsetRef = useRef(0);
  const paramsRef = useRef({ limit: DEFAULT_LIMIT, today_only: true });

  const loadRawStocks = useCallback(
    async (params = {}, options = {}) => {
      const append = Boolean(options.append);
      let requestParams;

      if (append) {
        const baseParams = paramsRef.current;
        const limitValue = Number(baseParams.limit ?? DEFAULT_LIMIT);

        if (!limitValue) {
          setHasMore(false);
          return [];
        }

        requestParams = {
          ...baseParams,
          offset: offsetRef.current,
        };
      } else {
        const mergedParams = {
          ...paramsRef.current,
          ...params,
        };

        Object.keys(mergedParams).forEach((key) => {
          if (
            mergedParams[key] === null ||
            mergedParams[key] === undefined ||
            mergedParams[key] === ''
          ) {
            delete mergedParams[key];
          }
        });

        if (params.limit !== undefined) {
          mergedParams.limit = params.limit;
        } else if (mergedParams.limit === undefined) {
          mergedParams.limit = DEFAULT_LIMIT;
        }

        if (params.today_only !== undefined) {
          mergedParams.today_only = params.today_only;
        } else if (mergedParams.today_only === undefined) {
          mergedParams.today_only = true;
        }

        paramsRef.current = mergedParams;
        requestParams = { ...paramsRef.current };
        offsetRef.current = 0;
        setHasMore(Number(requestParams.limit ?? DEFAULT_LIMIT) !== 0);
      }

      try {
        const data = await dispatch(fetchRawStocks(requestParams, { append }));
        const received = Array.isArray(data) ? data.length : 0;

        if (append) {
          offsetRef.current += received;
        } else {
          offsetRef.current = received;
        }

        const limitValue = Number(requestParams.limit ?? DEFAULT_LIMIT);

        if (!limitValue) {
          setHasMore(false);
        } else {
          setHasMore(received === limitValue);
        }

        return data;
      } catch (err) {
        setHasMore(false);
        return [];
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const initialise = async () => {
      await loadRawStocks({ limit: DEFAULT_LIMIT, today_only: true });
    };

    initialise();
    dispatch(fetchStocks());
    dispatch(fetchStones());
    dispatch(fetchPurities());
    dispatch(fetchColors());
  }, [dispatch, loadRawStocks]);

  const handleDelete = async (id) => {
    const result = await confirmDelete('Are you sure?', 'This raw stock entry will be deleted permanently!');
    if (result.isConfirmed) {
      dispatch(deleteRawStock(id)).then(() => {
        showSuccess('Deleted!', 'The raw stock entry has been deleted.');
      });
    }
  };

  const handleNew = () => {
    onEdit(null); // This will reset the form and show it in "Add" mode
  };

  const filteredRawStocks = useMemo(() => {
    const searchValue = filterText.toLowerCase();
    return rawStocks.filter((rawStock) => {
      const stockTypeName = stocks.find(s => s.id === rawStock.stock_type_id)?.name || '';
      const stoneTypeName = stones.find(s => s.id === rawStock.stone_type_id)?.name || '';
      const purityName = purities.find(p => p.id === rawStock.karatage)?.name || '';
      const colorName = colors.find(c => c.id === rawStock.color)?.name || '';

      return (
        stockTypeName.toLowerCase().includes(searchValue) ||
        stoneTypeName.toLowerCase().includes(searchValue) ||
        purityName.toLowerCase().includes(searchValue) ||
        colorName.toLowerCase().includes(searchValue) ||
        (rawStock.stock_code && rawStock.stock_code.toLowerCase().includes(searchValue)) ||
        (rawStock.stock_name && rawStock.stock_name.toLowerCase().includes(searchValue)) ||
        String(rawStock.qty).toLowerCase().includes(searchValue) ||
        String(rawStock.size).toLowerCase().includes(searchValue) ||
        String(rawStock.weight).toLowerCase().includes(searchValue) ||
        String(rawStock.reference_number).toLowerCase().includes(searchValue) ||
        String(rawStock.remarks).toLowerCase().includes(searchValue) ||
        (rawStock.created_by && rawStock.created_by.toLowerCase().includes(searchValue)) ||
        (rawStock.created_at && new Date(rawStock.created_at).toLocaleString().toLowerCase().includes(searchValue)) ||
        (rawStock.updated_at && new Date(rawStock.updated_at).toLocaleString().toLowerCase().includes(searchValue))
      );
    });
  }, [rawStocks, stocks, stones, purities, colors, filterText]);

  const handleClearFilters = async () => {
    setFilterText('');
    await loadRawStocks({ limit: DEFAULT_LIMIT, today_only: true, from_date: null, to_date: null });
  };

  const handleLoadRecent = async () => {
    await loadRawStocks({ limit: DEFAULT_LIMIT, today_only: true, from_date: null, to_date: null });
  };

  const handleLoadAll = async () => {
    await loadRawStocks({ limit: 0, today_only: false });
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      await loadRawStocks({}, { append: true });
    } finally {
      setLoadingMore(false);
    }
  };

  const columns = [
    {
      name: <Typography fontWeight="bold" fontSize="16px">Stock Type</Typography>,
      selector: (row) => stocks.find(s => s.id === row.stock_type_id)?.name || 'N/A',
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold" fontSize="16px">Stock Code</Typography>,
      selector: (row) => row.stock_code || 'N/A',
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold" fontSize="16px">Stock Name</Typography>,
      selector: (row) => row.stock_name || 'N/A',
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold" fontSize="16px">Quantity</Typography>,
      selector: (row) => row.qty,
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold" fontSize="16px">Stone Type</Typography>,
      selector: (row) => stones.find(s => s.id === row.stone_type_id)?.name || 'N/A',
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold" fontSize="16px">Karatage</Typography>,
      selector: (row) => purities.find(p => p.id === row.karatage)?.name || 'N/A',
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold" fontSize="16px">Color</Typography>,
      selector: (row) => colors.find(c => c.id === row.color)?.name || 'N/A',
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold" fontSize="16px">Size</Typography>,
      selector: (row) => row.size || 'N/A',
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold" fontSize="16px">Weight</Typography>,
      selector: (row) => row.weight,
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold" fontSize="16px">Reference Number</Typography>,
      selector: (row) => row.reference_number,
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold" fontSize="16px">Remarks</Typography>,
      selector: (row) => row.remarks || 'N/A',
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold" fontSize="16px">Created By</Typography>,
      selector: (row) => row.created_by || 'N/A',
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold" fontSize="16px">Created At</Typography>,
      selector: (row) => row.created_at ? new Date(row.created_at).toLocaleString() : 'N/A',
      sortable: true,
    },
    {
      name: <Typography fontWeight="bold" fontSize="16px">Updated At</Typography>,
      selector: (row) => row.updated_at ? new Date(row.updated_at).toLocaleString() : 'N/A',
      sortable: true,
    },
    ...(user?.role === 'Admin' ? [{
      name: <Typography fontWeight="bold" fontSize="16px">Actions</Typography>,
      cell: (row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={() => onEdit(row)}>
            <EditIcon fontSize="small" sx={{ color: '#2196f3' }} />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(row.id)}>
            <DeleteIcon fontSize="small" sx={{ color: '#f44336' }} />
          </IconButton>
        </Box>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    }] : []),
  ];

  return (
    <Box sx={{ overflowX: 'auto' }}>
      {/* Header Row with Title & Search */}
      <PageLoader loading={loading && !loadingMore} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Raw Stock Entries
          </Typography>
          {user?.role === 'Admin' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNew}
              size="small"
              sx={{
                backgroundColor: '#2196f3',
                '&:hover': {
                  backgroundColor: '#1976d2',
                },
              }}
            >
              New
            </Button>
          )}
        </Box>

        <TextField
          size="small"
          placeholder="Search raw stock..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          sx={{
            width: '250px',
            '& .MuiInputBase-root': {
              backgroundColor: 'transparent',
            },
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading data: {error}
        </Alert>
      )}

      {/* Table */}
      <DataTable
        columns={columns}
        data={filteredRawStocks}
        pagination
        highlightOnHover
        dense
        responsive
        noHeader
        progressPending={loading && !loadingMore}
        progressComponent={<CircularProgress />}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          mt: 2,
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClearFilters}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Clear & Reload
        </Button>
        <Button
          variant="outlined"
          onClick={handleLoadRecent}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Load Recent
        </Button>
        <Button
          variant="outlined"
          onClick={handleLoadAll}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Load All
        </Button>
        {hasMore && (
          <Button
            variant="contained"
            onClick={handleLoadMore}
            disabled={loading || loadingMore}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {loadingMore ? 'Loading…' : 'Load More'}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default RawStockTable; 